'use client';

import type { Money } from '@/lib/shopify/types';
import { formatPrice } from '@/lib/shopify';

interface DigitisingFeeSelectorProps {
  frontSelected: boolean;
  backSelected: boolean;
  onFrontChange: (checked: boolean) => void;
  onBackChange: (checked: boolean) => void;
  frontPrice: Money;
  backPrice: Money;
  // Disabled when toggling would target a Finish variant that's out of stock
  // for the current Colour/Size — mirrors the greyed-out top Finish buttons.
  frontDisabled?: boolean;
  backDisabled?: boolean;
}

export default function DigitisingFeeSelector({
  frontSelected,
  backSelected,
  onFrontChange,
  onBackChange,
  frontPrice,
  backPrice,
  frontDisabled = false,
  backDisabled = false,
}: DigitisingFeeSelectorProps) {
  return (
    <div className="border border-[var(--color-border)] p-4 space-y-3">
      <div>
        <span className="block text-sm font-medium uppercase tracking-wider">
          Embroidery Digitising
        </span>
        <p className="text-xs text-[var(--color-text-secondary)] mt-1">
          A one-time setup fee per embroidered side. It is added automatically as a separate cart line and reused across sizes for the same product.
        </p>
      </div>

      <div className="space-y-2">
        {/* A checked fee is required by the selected Finish and cannot be
            removed. An unchecked side can still be selected to upgrade the
            garment to front-and-back embroidery. */}
        <label
          className={`flex items-center gap-3 ${frontSelected || frontDisabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
          title={frontSelected ? 'Included automatically for this embroidered finish' : frontDisabled ? 'Not available for this colour / size combination' : 'Add front embroidery'}
        >
          <input
            type="checkbox"
            checked={frontSelected}
            onChange={(e) => onFrontChange(e.target.checked)}
            disabled={frontSelected || frontDisabled}
            aria-label={frontSelected ? 'Front digitising included automatically' : 'Add front embroidery and digitising'}
            className="w-4 h-4 accent-black cursor-pointer disabled:cursor-not-allowed"
          />
          <span className="text-sm text-[var(--color-text-primary)]">
            {frontSelected ? 'Front digitising' : 'Add front embroidery'} — {formatPrice(frontPrice)}
            {frontSelected && <span className="ml-2 text-xs uppercase tracking-wider text-[var(--color-text-secondary)]">included automatically</span>}
            {frontDisabled && !frontSelected && <span className="ml-2 text-xs uppercase tracking-wider text-[var(--color-text-muted)]">unavailable</span>}
          </span>
        </label>

        <label
          className={`flex items-center gap-3 ${backSelected || backDisabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
          title={backSelected ? 'Included automatically for this embroidered finish' : backDisabled ? 'Not available for this colour / size combination' : 'Add back embroidery'}
        >
          <input
            type="checkbox"
            checked={backSelected}
            onChange={(e) => onBackChange(e.target.checked)}
            disabled={backSelected || backDisabled}
            aria-label={backSelected ? 'Back digitising included automatically' : 'Add back embroidery and digitising'}
            className="w-4 h-4 accent-black cursor-pointer disabled:cursor-not-allowed"
          />
          <span className="text-sm text-[var(--color-text-primary)]">
            {backSelected ? 'Back digitising' : 'Add back embroidery'} — {formatPrice(backPrice)}
            {backSelected && <span className="ml-2 text-xs uppercase tracking-wider text-[var(--color-text-secondary)]">included automatically</span>}
            {backDisabled && !backSelected && <span className="ml-2 text-xs uppercase tracking-wider text-[var(--color-text-muted)]">unavailable</span>}
          </span>
        </label>
      </div>

      <p className="text-xs text-[var(--color-text-muted)]">
        Returning customer with existing artwork? <a href="/contact" className="underline hover:text-black">Contact us</a> for adjusted pricing.
      </p>
    </div>
  );
}
